import { createSelector } from '@reduxjs/toolkit';

import { FeatureFlag } from 'protocol/model/dto_organization_pb';
import {
  OrganizationStaff,
  OrganizationStaffRoleType,
} from 'protocol/model/dto_organization_staff_pb';

import { RootState } from '@/app/model/store';

export const selectOrganizationLoading = (state: RootState) => state.organization.loading;

export const selectOrganization = (state: RootState) => state.organization.organization;

export const selectParentDSOOrganization = (state: RootState) => state.organization.parentDSOOrganization;

export const selectOrganizationStorageSize = (state: RootState) => state.organization.storageSize;

export const selectOrganizationStaff = (state: RootState) => state.organization.staff;

export const selectFeatureFlagEnabled =
  (featureFlag: FeatureFlag) => (state: RootState) =>
    state.organization.organization?.FeatureFlags?.some(
      ({ Flag, Value }) => Flag === featureFlag && Value,
    ) ?? false;

const STAFF_ROLE_ORDER: Record<number, number> = {
  [OrganizationStaffRoleType.OrganizationStaffRoleType_FullAccess]: 0,
  [OrganizationStaffRoleType.OrganizationStaffRoleType_ClinicalAccess]: 1,
  [OrganizationStaffRoleType.OrganizationStaffRoleType_LimitedClinicalAccess]: 2,
  [OrganizationStaffRoleType.OrganizationStaffRoleType_NonClinicalMember]: 3,
  [OrganizationStaffRoleType.OrganizationStaffRoleType_InvalidValue]: 4,
};

const getStaffGroupOrder = (staff: OrganizationStaff) => {
  const isActive = staff.Status?.case === 'Active';
  const isRegistered = Boolean(staff.UserID);

  if (isActive && isRegistered) { return 0; }
  if (isActive && !isRegistered) { return 1; }
  if (!isActive && isRegistered) { return 2; }
  return 3;
};

export const selectSortedOrganizationStaff = createSelector(
  selectOrganizationStaff,
  (staff) =>
    [...staff].sort((a, b) => {
      const groupDiff = getStaffGroupOrder(a) - getStaffGroupOrder(b);

      if (groupDiff !== 0) {
        return groupDiff;
      }

      return (STAFF_ROLE_ORDER[a.Role] ?? 4) - (STAFF_ROLE_ORDER[b.Role] ?? 4);
    }),
);

// NOTE: The organization payload carries only the owner email, so the owner's
// userID and name are resolved by matching that email against the staff list.
export const selectOrganizationOwner = createSelector(
  selectOrganization,
  selectOrganizationStaff,
  (organization, staff) => {
    const ownerEmail = organization?.OwnerEmail?.trim().toLowerCase();

    if (!ownerEmail) {
      return undefined;
    }

    const owner = staff.find(
      (member) =>
        Boolean(member.UserID) &&
        member.PersonalData?.Email?.trim().toLowerCase() === ownerEmail,
    );

    if (!owner) {
      return undefined;
    }

    const { FirstName, LastName } = owner.PersonalData ?? {};

    return {
      userID: owner.UserID,
      fullName: `${FirstName ?? ''} ${LastName ?? ''}`.trim(),
    };
  },
);
